import React, { ReactElement, useState } from 'react'
import { Formik, Form } from 'formik'
import NFTInput from '../../../components/NFTInput/NFTInput'
import NFTUpload from '../../../components/NFTUpload/NFTUpload'
import NFTCreateModal from '../../../components/NFTCreateModal'
import { INFTCreateFormValues, INFTFormErrors } from '../../../models/INFTCreateERC721'
import {
  useCalculateMintFee,
  useGetBaseNFTMintFeePrice,
  useGetMaxCreatorFeeRate
} from '../../../../../hooks/useYetiSwapNFTCollection'
import { YETISWAP_NFT_COLLECTION_ADDRESS } from '../../../../../constants'
import { ChainId } from '@yetiswap/sdk'
import { useActiveWeb3React } from '../../../../../hooks'
import NFTGoBack from '../../../components/NFTGoBack'
import INFTProperties from '../../../models/INFTProperties'
import {
  Content,
  ContentForm,
  Title,
  PageWrapper,
  CreateWrapper,
  ContnetPreview,
  ContentLabel,
  PropertiesContainer,
  NFTCreateButton,
  CreateFeeDiv
} from './NTFCreateERC721Styled'
import {
  validateExtendUrl,
  validateFile,
  validateProperties,
  validateRoyalties,
  validateTitle
} from './NFTCreateERC721Validate'
import NFTRangeSlider from '../../../components/NFTRangeSlider'
import { INTFFileUpload } from './INTFCreateERC721'

const formInitialValues = {
  title: '',
  description: '',
  royalties: 5,
  nftFile: null,
  coverFile: null,
  nftProperties: '',
  externalUrl: ''
}

const initialFormError = {
  title: '',
  royalties: '',
  nftFile: '',
  coverFile: '',
  nftProperties: ''
}

const initialNFTProperties: INFTProperties = {
  key: '',
  value: '',
  show: true,
  isUrl: false
}

export default function NTFCreateERC721() {
  const { chainId } = useActiveWeb3React()
  const [nftFile, setNFTFile] = useState<INTFFileUpload | null>(null)
  const [coverFile, setCoverFile] = useState<INTFFileUpload | null>(null)
  const [nftTitle, setNftTitle] = useState('')
  const [properties, setProperties] = useState<any[]>([initialNFTProperties])
  const [nftFormData, setNftFormData] = useState<INFTCreateFormValues>(formInitialValues)
  const [royalties, setRoyalties] = useState(formInitialValues.royalties)
  const [formError, setFormError] = useState<INFTFormErrors>(initialFormError)
  const [createModalIsOpen, setCreateModalIsOpen] = useState(false)
  const baseNftMintFeePrice = useGetBaseNFTMintFeePrice()
  const maxCreatorFeeRate = useGetMaxCreatorFeeRate()
  const calculateMintFee = useCalculateMintFee(baseNftMintFeePrice, royalties)
  const mintFeeText = calculateMintFee.toSignificant(4) !== '0' ? calculateMintFee.toSignificant(4) + ' YTS' : ''
  const collectionAddress = chainId
    ? YETISWAP_NFT_COLLECTION_ADDRESS[chainId]
    : YETISWAP_NFT_COLLECTION_ADDRESS[ChainId.AVALANCHE]

  const fileType = nftFile ? nftFile.file.type.split('/')[0] : 'image'

  function onSubmit(values: INFTCreateFormValues) {
    const formErrors = {
      ...initialFormError,
      title: validateTitle(values.title),
      nftFile: validateFile(nftFile?.file, 'file'),
      coverFile: fileType !== 'image' ? validateFile(coverFile, 'cover') : '',
      royalties: validateRoyalties(royalties, maxCreatorFeeRate),
      nftProperties: validateProperties(properties),
      externalUrl: validateExtendUrl(values.externalUrl)
    }

    setFormError(formErrors)

    if (
      !formErrors.title &&
      !formErrors.nftFile &&
      !formErrors.coverFile &&
      !formErrors.royalties &&
      !formErrors.nftProperties &&
      !formErrors.externalUrl
    ) {
      setNftFormData({
        ...values,
        royalties: royalties
      })
      setCreateModalIsOpen(true)
    }
  }

  function cancelCreateModal() {
    setCreateModalIsOpen(false)
  }

  function onChangeRoyalties(value: string) {
    setRoyalties(parseInt(value))
  }

  function onChangeTitle(value: string) {
    setNftTitle(value)
    if (value && formError.title) {
      setFormError({ ...formError, title: validateTitle(nftTitle) })
    }
  }

  function removeFile(type: 'file' | 'cover') {
    if (type === 'cover') {
      setCoverFile(null)
    } else if (type === 'file') {
      setNFTFile(null)
      setCoverFile(null)
    }
  }

  function onChangeExtendUrl(val: string) {
    const CheckExtendUrl = validateExtendUrl(val)
    if (CheckExtendUrl) {
      setFormError({ ...formError, externalUrl: CheckExtendUrl })
    } else {
      setFormError({ ...formError, externalUrl: undefined })
    }
  }

  function onChangeFile(value: INTFFileUpload | null, type: 'file' | 'cover') {
    if (type === 'cover') {
      setCoverFile(value)
    } else if (type === 'file') {
      setNFTFile(value)
    }
    if (value && type === 'file' && formError.nftFile) {
      setFormError({ ...formError, nftFile: validateFile(value, type) })
    } else if (value && type === 'cover' && formError.coverFile) {
      setFormError({ ...formError, coverFile: validateFile(value, type) })
    }
  }

  function onChangePropsKeyValue(index: number, type: string, value: string) {
    const oldProperties = [...properties]
    const oldPropertiesLength = oldProperties.length
    const lastItem = oldProperties[oldPropertiesLength - 1]
    const foundedItem = oldProperties[index]
    const showingProps = oldProperties.filter(item => item.show)

    if (foundedItem) {
      oldProperties[index] = {
        ...foundedItem,
        [type]: value
      }

      if (lastItem.value && lastItem.key) {
        oldProperties.push(initialNFTProperties)
      }

      if (
        (!foundedItem.value || (type === 'value' && !value)) &&
        (!foundedItem.key || (type === 'key' && !value)) &&
        !value &&
        showingProps.length > 1
      ) {
        oldProperties[index] = {
          ...foundedItem,
          show: false
        }
      }

      setProperties(oldProperties)
      if (value && formError.nftProperties) {
        setFormError({ ...formError, nftProperties: validateProperties(properties) })
      }
    }
  }

  function getPreview() {
    let result: string | ReactElement = 'Upload file to preview your new NFT'
    if (nftFile && fileType === 'image') {
      result = <img src={URL.createObjectURL(nftFile.file)} alt="NFT Preview" />
    } else if (coverFile) {
      result = <img src={URL.createObjectURL(coverFile.file)} alt="NFT Preview" />
    }
    return result
  }

  return (
    <PageWrapper>
      <CreateWrapper>
        <NFTGoBack text="Manage collectible type" url={'/nft-create'} />
        <Title>Create single collectible</Title>
        <Content>
          <ContentForm>
            <Formik onSubmit={(values: any) => onSubmit(values)} initialValues={formInitialValues}>
              {() => (
                <Form encType="multipart/form-data" autoComplete="off">
                  <NFTUpload
                    accept="image/png,image/jpeg,image/gif,image/webp,video/mp4,video/webm,audio/mp3,audio/webm,audio/mpeg"
                    description="PNG, GIF, WEBP, MP4 or MP3. Max 10mb."
                    id="nftFile"
                    name="nftFile"
                    label="Upload file"
                    setFieldValue={(value: INTFFileUpload | null) => onChangeFile(value, 'file')}
                    removeFile={() => removeFile('file')}
                    value={nftFile}
                    errorMessage={formError.nftFile}
                  />
                  {fileType !== 'image' && (
                    <NFTUpload
                      accept="image/png,image/jpeg,image/gif,image/webp"
                      description="JPG, PNG, GIF or WEBP. Max 10mb."
                      id="coverImage"
                      name="coverImage"
                      label="Upload cover"
                      setFieldValue={(value: INTFFileUpload | null) => onChangeFile(value, 'cover')}
                      value={coverFile}
                      errorMessage={formError.coverFile}
                      removeFile={() => removeFile('cover')}
                    />
                  )}

                  <NFTInput
                    id="title"
                    name="title"
                    label="Name"
                    placeholder={'e. g. "Redeemable T-Shirt with logo"'}
                    errorMessage={formError.title}
                    onKeyUp={onChangeTitle}
                  />

                  <NFTInput
                    id="description"
                    name="description"
                    label="Description"
                    optional={true}
                    placeholder={'e. g. "After purchasing you will be able to get the real T-Shirt"'}
                    description="With preserved line-breaks"
                  />

                  <NFTInput
                    id="externalUrl"
                    name="externalUrl"
                    label="External Url"
                    placeholder={'e. g. "https://yetiswap.app"'}
                    optional={true}
                    errorMessage={formError.externalUrl}
                    onKeyUp={onChangeExtendUrl}
                  />

                  <NFTRangeSlider
                    id="royalties"
                    name="royalties"
                    label="Royalties"
                    max={maxCreatorFeeRate}
                    min={0}
                    placeholder={'e. g. "10%"'}
                    errorMessage={formError.royalties}
                    description="Suggested: 5%, 10%, 15%"
                    onKeyUp={onChangeRoyalties}
                    value={royalties}
                  />

                  <PropertiesContainer>
                    <ContentLabel>
                      Properties <span>(Optional)</span>
                    </ContentLabel>
                    <div className="props-list">
                      {properties.map((item: INFTProperties, index: number) => {
                        const keyName = `${index}_NFT_KEY`
                        const valueName = `${index}_NFT_VALUE`
                        return item.show ? (
                          <div className="prop-item" key={`${index}_NFT_PROPERTIES`}>
                            <NFTInput
                              id={keyName}
                              name="nftKey"
                              label={''}
                              placeholder="e.g Size"
                              onKeyUp={(val: string) => onChangePropsKeyValue(index, 'key', val)}
                              isValid={!formError.nftProperties}
                            />
                            <NFTInput
                              id={valueName}
                              name="nftValue"
                              label={''}
                              placeholder="e.g M"
                              onKeyUp={(val: string) => onChangePropsKeyValue(index, 'value', val)}
                              isValid={!formError.nftProperties}
                            />
                          </div>
                        ) : (
                          <React.Fragment />
                        )
                      })}
                      {formError.nftProperties && <div className="properties-error">{formError.nftProperties}</div>}
                    </div>
                  </PropertiesContainer>

                  {mintFeeText && <CreateFeeDiv>You will pay {mintFeeText} for these royalties</CreateFeeDiv>}
                  <NFTCreateButton type="submit">Create Item {mintFeeText}</NFTCreateButton>
                </Form>
              )}
            </Formik>
          </ContentForm>
          <ContnetPreview>
            <div className="preview-container">
              <div className="preview-title">Preview</div>
              <div className="preview-content">{getPreview()}</div>
            </div>
          </ContnetPreview>
        </Content>
      </CreateWrapper>

      <NFTCreateModal
        isOpen={createModalIsOpen}
        onDismiss={() => cancelCreateModal()}
        nftFile={nftFile?.file}
        coverFile={coverFile?.file}
        nftFormData={nftFormData}
        properties={properties}
        mintFee={calculateMintFee}
        collectionAddress={collectionAddress}
      />
    </PageWrapper>
  )
}
